import { useEffect, useState } from "react";

const AUTO_CANCEL_SEC = 120;

/**
 * CC AskUserQuestion 问题卡片（多问题逐一收集，全部回答后一次提交）
 *
 * Props:
 *   pendingQuestion  { tool_use_id, questions: [{question, header, options:[{label,description}], multiSelect}] }
 *   onRespond        (toolUseId: string, answers: [{question, answer}]) => void
 */
const CCQuestionCard = ({ pendingQuestion, onRespond }) => {
  const [countdown, setCountdown] = useState(AUTO_CANCEL_SEC);
  // answers: { [qi]: string | string[] }
  const [answers, setAnswers] = useState({});
  const [customOpen, setCustomOpen] = useState({});
  const [customText, setCustomText] = useState({});
  const [onlyCustom, setOnlyCustom] = useState({});

  useEffect(() => {
    if (!pendingQuestion) return;
    setCountdown(AUTO_CANCEL_SEC);
    setAnswers({});
    setCustomOpen({});
    setCustomText({});
    setOnlyCustom({});
    const timer = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(timer);
          // 超时：把已选的提交，未选的填"取消"
          const qs = pendingQuestion.questions || [];
          const finalAnswers = qs.map((q, qi) => ({
            question: q.question,
            answer: answers[qi] ?? "取消",
          }));
          onRespond(pendingQuestion.tool_use_id, finalAnswers);
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingQuestion]);

  if (!pendingQuestion) return null;

  const { tool_use_id, questions } = pendingQuestion;
  const buildAnswer = (qi) => {
    const custom = (customText[qi] || "").trim();
    if (onlyCustom[qi]) return custom;
    const value = answers[qi];
    const base = Array.isArray(value) ? value.join(", ") : (value || "");
    if (base && custom) return `${base}；补充说明：${custom}`;
    return custom || base;
  };
  const allAnswered = questions.every((q, qi) => buildAnswer(qi, q).trim() !== "");

  const handleSelect = (qi, q, label) => {
    const current = answers[qi];
    const nextValue = q.multiSelect
      ? toggleArrayValue(Array.isArray(current) ? current : [], label)
      : label;
    const next = { ...answers, [qi]: nextValue };
    setAnswers(next);
    if (isCustomLabel(label)) {
      setCustomOpen((prev) => ({ ...prev, [qi]: true }));
    }
    // 单选单题仍保留快速提交；多选/自定义需要用户点确认
    if (questions.length === 1 && !q.multiSelect && !isCustomLabel(label)) {
      onRespond(tool_use_id, [{ question: questions[0].question, answer: label }]);
    }
  };

  const handleSubmit = () => {
    const finalAnswers = questions.map((q, qi) => ({
      question: q.question,
      answer: buildAnswer(qi, q) || "取消",
    }));
    onRespond(tool_use_id, finalAnswers);
  };

  return (
    <div style={{
      margin: "8px 0",
      border: "1px solid #d29922",
      borderRadius: "8px",
      background: "#1c1a10",
      overflow: "hidden",
    }}>
      {/* 标题栏 */}
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        padding: "8px 14px",
        borderBottom: "1px solid #2d2a18",
        background: "#211e0e",
      }}>
        <span style={{ fontSize: "14px" }}>❓</span>
        <span style={{ color: "#d29922", fontWeight: 600, fontSize: "13px" }}>
          YouLabber 需要你的决策
        </span>
        <span style={{ marginLeft: "auto", color: "#6e7681", fontSize: "11px" }}>
          {countdown}s 后自动取消
        </span>
      </div>

      {/* 问题列表 */}
      <div style={{ padding: "12px 14px", display: "flex", flexDirection: "column", gap: "14px" }}>
        {questions.map((q, qi) => (
          <div key={qi}>
            {q.header && (
              <div style={{ color: "#8b949e", fontSize: "11px", marginBottom: "4px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                {q.header}
              </div>
            )}
            <div style={{ color: "#e6edf3", fontSize: "13px", marginBottom: "10px", lineHeight: 1.5 }}>
              {q.question}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              {(q.options || []).map((opt, oi) => {
                const label = typeof opt === "string" ? opt : opt.label;
                const desc = typeof opt === "string" ? "" : opt.description;
                const current = answers[qi];
                const isSelected = q.multiSelect
                  ? Array.isArray(current) && current.includes(label)
                  : current === label;
                return (
                  <button
                    key={oi}
                    onClick={() => handleSelect(qi, q, label)}
                    style={{
                      background: isSelected ? "#1c2d1c" : "#161b22",
                      border: `1px solid ${isSelected ? "#3fb950" : "#30363d"}`,
                      borderRadius: "6px",
                      color: "#e6edf3",
                      padding: "8px 12px",
                      cursor: "pointer",
                      textAlign: "left",
                      fontSize: "13px",
                      transition: "border-color 0.15s, background 0.15s",
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.borderColor = "#d29922";
                        e.currentTarget.style.background = "#1c2128";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.borderColor = "#30363d";
                        e.currentTarget.style.background = "#161b22";
                      }
                    }}
                  >
                    <div style={{ fontWeight: 500, color: isSelected ? "#3fb950" : "#f0c040" }}>
                      {isSelected ? "✓ " : ""}{label}
                    </div>
                    {desc && (
                      <div style={{ color: "#8b949e", fontSize: "12px", marginTop: "2px" }}>{desc}</div>
                    )}
                  </button>
                );
              })}
            </div>
            <div style={{ marginTop: "8px", borderTop: "1px dashed #30363d", paddingTop: "8px" }}>
              {!customOpen[qi] ? (
                <button
                  onClick={() => setCustomOpen((prev) => ({ ...prev, [qi]: true }))}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: "#58a6ff",
                    cursor: "pointer",
                    padding: 0,
                    fontSize: "12px",
                    textDecoration: "underline",
                  }}
                >
                  选项里没有我要的答案 / 我要补充说明
                </button>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                  <label style={{ color: "#8b949e", fontSize: "12px" }}>
                    <input
                      type="checkbox"
                      checked={!!onlyCustom[qi]}
                      onChange={(e) => setOnlyCustom((prev) => ({ ...prev, [qi]: e.target.checked }))}
                      style={{ marginRight: 6 }}
                    />
                    仅使用我在下方填写的答案
                  </label>
                  <textarea
                    rows={3}
                    value={customText[qi] || ""}
                    onChange={(e) => setCustomText((prev) => ({ ...prev, [qi]: e.target.value }))}
                    placeholder="填写自定义答案或补充说明"
                    style={{
                      width: "100%",
                      boxSizing: "border-box",
                      background: "#0d1117",
                      border: "1px solid #30363d",
                      borderRadius: "6px",
                      color: "#e6edf3",
                      padding: "8px",
                      resize: "vertical",
                      fontFamily: "inherit",
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        ))}

        {/* 多问题、多选或自定义时显示提交按钮 */}
        {(questions.length > 1 || questions.some((q) => q.multiSelect) || Object.values(customOpen).some(Boolean)) && (
          <button
            onClick={handleSubmit}
            disabled={!allAnswered}
            style={{
              background: allAnswered ? "#238636" : "#21262d",
              border: "none",
              borderRadius: "6px",
              color: allAnswered ? "#fff" : "#6e7681",
              padding: "8px 16px",
              cursor: allAnswered ? "pointer" : "not-allowed",
              fontSize: "13px",
              fontWeight: 600,
              alignSelf: "flex-end",
              marginTop: "4px",
            }}
          >
            {allAnswered ? "确认提交" : `还需回答 ${questions.filter((_, qi) => answers[qi] === undefined).length} 个问题`}
          </button>
        )}
      </div>
    </div>
  );
};

const toggleArrayValue = (values, label) => (
  values.includes(label) ? values.filter((item) => item !== label) : [...values, label]
);

const isCustomLabel = (label) => /其他|其它|自定义|补充|另行|以上都不|不在/.test(String(label || ""));

export default CCQuestionCard;
